<script>
  import { onMount } from 'svelte';
  import Layout from '../components/Layout.svelte';
  import TimesheetModal from '../components/TimesheetModal.svelte';
  import ConfirmModal from '../components/ConfirmModal.svelte';
  import { timesheetsApi, customersApi } from '../stores/api.js';
  import { _ } from '../lib/i18n.js';
  import {
    Clock, Plus, Edit2, Trash2, Loader, Calendar, DollarSign,
    Filter, Calculator, ChevronDown, CheckCircle, XCircle, Receipt
  } from 'lucide-svelte';

  let timesheets = [];
  let customers = [];
  let loading = true;
  let error = '';
  let message = null;

  // Filters
  let startDate = '';
  let endDate = '';
  let customerId = '';
  let billableFilter = '';

  // Pagination
  let currentPage = 1;
  let totalPages = 1;
  let total = 0;
  const limit = 20;

  // Modals
  let showTimesheetModal = false;
  let editingTimesheet = null;
  let showConfirmModal = false;
  let deletingTimesheet = null;

  // Summary
  let showSummary = false;
  let summaryData = null;
  let loadingSummary = false;
  let summaryStartDate = '';
  let summaryEndDate = '';

  onMount(async () => {
    // Set default dates for current month
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    summaryStartDate = firstDay.toISOString().split('T')[0];
    summaryEndDate = lastDay.toISOString().split('T')[0];

    await Promise.all([loadTimesheets(), loadCustomers()]);
  });

  async function loadTimesheets() {
    loading = true;
    error = '';
    try {
      console.log('[Timesheets] Loading timesheets...');
      const params = { page: currentPage, limit };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (customerId) params.customerId = customerId;
      if (billableFilter !== '') params.billable = billableFilter;

      const response = await timesheetsApi.getAll(params);
      timesheets = response.timesheets || [];
      total = response.pagination?.total || 0;
      totalPages = response.pagination?.pages || 1;
      console.log(`[Timesheets] Loaded ${timesheets.length} timesheets`);
    } catch (err) {
      console.error('[Timesheets] Error loading:', err);
      error = err.message;
    } finally {
      loading = false;
    }
  }

  async function loadCustomers() {
    try {
      const response = await customersApi.getAll({ limit: 1000 });
      customers = response.customers || [];
    } catch (err) {
      console.error('[Timesheets] Error loading customers:', err);
    }
  }

  async function loadSummary() {
    if (!summaryStartDate || !summaryEndDate) {
      return;
    }
    loadingSummary = true;
    try {
      console.log(`[Timesheets] Loading summary for ${summaryStartDate} to ${summaryEndDate}`);
      summaryData = await timesheetsApi.getSummary(summaryStartDate, summaryEndDate);
      console.log('[Timesheets] Summary loaded:', summaryData);
    } catch (err) {
      console.error('[Timesheets] Error loading summary:', err);
      message = { type: 'error', text: err.message };
    } finally {
      loadingSummary = false;
    }
  }

  function handleAddClick() {
    editingTimesheet = null;
    showTimesheetModal = true;
  }

  function handleEditClick(timesheet) {
    editingTimesheet = timesheet;
    showTimesheetModal = true;
  }

  function handleDeleteClick(timesheet) {
    deletingTimesheet = timesheet;
    showConfirmModal = true;
  }

  // The modal owns the create/update call itself (like ProductVariantModal
  // and CustomerPricesModal) so a server-side error can be shown inside the
  // modal, which sits above this page's own message banner. It only
  // dispatches 'success' once the save has actually gone through.
  async function handleTimesheetSaved() {
    message = { type: 'success', text: editingTimesheet ? $_('timesheets.messages.updated') : $_('timesheets.messages.created') };
    showTimesheetModal = false;
    editingTimesheet = null;
    await loadTimesheets();
  }

  async function handleConfirmDelete() {
    if (!deletingTimesheet) return;
    try {
      await timesheetsApi.delete(deletingTimesheet.id);
      message = { type: 'success', text: $_('timesheets.messages.deleted') };
      showConfirmModal = false;
      deletingTimesheet = null;
      await loadTimesheets();
    } catch (err) {
      console.error('[Timesheets] Error deleting:', err);
      message = { type: 'error', text: err.message };
    }
  }

  function handleFilterChange() {
    currentPage = 1;
    loadTimesheets();
  }

  function clearFilters() {
    startDate = '';
    endDate = '';
    customerId = '';
    billableFilter = '';
    currentPage = 1;
    loadTimesheets();
  }

  function getCustomerDisplayName(customer) {
    if (!customer) return '-';
    if (customer.companyName) return customer.companyName;
    if (customer.name || customer.surname) {
      return `${customer.name || ''} ${customer.surname || ''}`.trim();
    }
    return customer.id;
  }

  function formatDate(dateString) {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  function formatHours(hours) {
    return hours?.toFixed(2) || '0.00';
  }

  function formatCurrency(amount) {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  }

  function getActivityTypeBadgeClass(activityType) {
    const classes = {
      development: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
      planning: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
      admin: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
      sales: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
      consulting: 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300'
    };
    return classes[activityType] || 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400';
  }

  async function toggleBilled(timesheet) {
    try {
      const newBilled = !timesheet.billed;
      console.log(`[Timesheets] Toggling billed for ${timesheet.id} to ${newBilled}`);
      await timesheetsApi.setBilled(timesheet.id, newBilled);
      timesheet.billed = newBilled;
      timesheets = [...timesheets];
      message = { type: 'success', text: newBilled ? $_('timesheets.messages.markedBilled') : $_('timesheets.messages.markedUnbilled') };
    } catch (err) {
      console.error('[Timesheets] Error toggling billed:', err);
      message = { type: 'error', text: err.message };
    }
  }

  $: hasFilters = startDate || endDate || customerId || billableFilter !== '';
</script>

<Layout>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div class="flex items-center space-x-4">
        <div class="p-3 bg-linear-to-br from-primary-500 to-primary-600 rounded-2xl shadow-lg">
          <Clock size={28} class="text-white" />
        </div>
        <div>
          <h1 class="text-2xl font-bold text-gray-900 dark:text-white">{$_('timesheets.title')}</h1>
          <p class="text-sm text-gray-500 dark:text-gray-400">{$_('timesheets.subtitle')}</p>
        </div>
      </div>

      <div class="flex items-center space-x-3">
        <button
          class="btn btn-secondary flex items-center space-x-2"
          onclick={() => showSummary = !showSummary}
        >
          <Calculator size={18} />
          <span>{$_('timesheets.summary.title')}</span>
        </button>
        <button class="btn btn-primary flex items-center space-x-2" onclick={handleAddClick}>
          <Plus size={18} />
          <span>{$_('timesheets.add')}</span>
        </button>
      </div>
    </div>

    <!-- Message -->
    {#if message}
      <div class="p-4 rounded-lg flex items-center justify-between {message.type === 'success' ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'}">
        <div class="flex items-center space-x-2">
          {#if message.type === 'success'}
            <CheckCircle size={20} />
          {:else}
            <XCircle size={20} />
          {/if}
          <span>{message.text}</span>
        </div>
        <button class="hover:opacity-70" onclick={() => message = null}>&times;</button>
      </div>
    {/if}

    <!-- Summary Panel -->
    {#if showSummary}
      <div class="card p-6">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">{$_('timesheets.summary.title')}</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">{$_('timesheets.summary.subtitle')}</p>

        <div class="flex items-end space-x-4 mb-6">
          <div>
            <label class="label">{$_('timesheets.filters.startDate')}</label>
            <input type="date" bind:value={summaryStartDate} class="input" />
          </div>
          <div>
            <label class="label">{$_('timesheets.filters.endDate')}</label>
            <input type="date" bind:value={summaryEndDate} class="input" />
          </div>
          <button class="btn btn-primary" onclick={loadSummary} disabled={loadingSummary}>
            {#if loadingSummary}
              <Loader size={18} class="animate-spin mr-2" />
            {/if}
            {$_('timesheets.summary.calculate')}
          </button>
        </div>

        {#if summaryData}
          <!-- Summary Totals -->
          <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div class="bg-primary-50 dark:bg-primary-900/20 rounded-lg p-4">
              <p class="text-sm text-primary-600 dark:text-primary-400">{$_('timesheets.summary.billableHours')}</p>
              <p class="text-2xl font-bold text-primary-700 dark:text-primary-300">{formatHours(summaryData.totals.billableHours)}h</p>
            </div>
            <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
              <p class="text-sm text-gray-600 dark:text-gray-400">{$_('timesheets.summary.nonBillableHours')}</p>
              <p class="text-2xl font-bold text-gray-700 dark:text-gray-300">{formatHours(summaryData.totals.nonBillableHours)}h</p>
            </div>
            <div class="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
              <p class="text-sm text-blue-600 dark:text-blue-400">{$_('timesheets.summary.totalHours')}</p>
              <p class="text-2xl font-bold text-blue-700 dark:text-blue-300">{formatHours(summaryData.totals.totalHours)}h</p>
            </div>
            <div class="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
              <p class="text-sm text-green-600 dark:text-green-400">{$_('timesheets.summary.billableAmount')}</p>
              <p class="text-2xl font-bold text-green-700 dark:text-green-300">{formatCurrency(summaryData.totals.billableAmount)}</p>
            </div>
          </div>

          <!-- Summary by Customer -->
          {#if summaryData.summary.length > 0}
            <div class="overflow-x-auto">
              <table class="w-full">
                <thead class="bg-gray-50 dark:bg-gray-800/50">
                  <tr>
                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('timesheets.fields.customer')}</th>
                    <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('timesheets.summary.billableHours')}</th>
                    <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('timesheets.summary.nonBillableHours')}</th>
                    <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('timesheets.summary.billableAmount')}</th>
                    <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('timesheets.summary.entries')}</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
                  {#each summaryData.summary as row}
                    <tr class="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td class="px-4 py-3 font-medium text-gray-900 dark:text-white">
                        {row.customerName || $_('timesheets.summary.noCustomer')}
                      </td>
                      <td class="px-4 py-3 text-right text-gray-600 dark:text-gray-300">{formatHours(row.billableHours)}h</td>
                      <td class="px-4 py-3 text-right text-gray-500 dark:text-gray-400">{formatHours(row.nonBillableHours)}h</td>
                      <td class="px-4 py-3 text-right font-medium text-green-600 dark:text-green-400">{formatCurrency(row.billableAmount)}</td>
                      <td class="px-4 py-3 text-right text-gray-500 dark:text-gray-400">{row.entriesCount}</td>
                    </tr>
                  {/each}
                </tbody>
              </table>
            </div>
          {:else}
            <p class="text-center text-gray-500 dark:text-gray-400 py-8">{$_('timesheets.noData')}</p>
          {/if}
        {/if}
      </div>
    {/if}

    <!-- Filters -->
    <div class="card p-4">
      <div class="flex flex-wrap items-end gap-4">
        <div>
          <label class="label">{$_('timesheets.filters.startDate')}</label>
          <input type="date" bind:value={startDate} onchange={handleFilterChange} class="input" />
        </div>
        <div>
          <label class="label">{$_('timesheets.filters.endDate')}</label>
          <input type="date" bind:value={endDate} onchange={handleFilterChange} class="input" />
        </div>
        <div>
          <label class="label">{$_('timesheets.fields.customer')}</label>
          <select bind:value={customerId} onchange={handleFilterChange} class="input">
            <option value="">{$_('timesheets.filters.allCustomers')}</option>
            {#each customers as customer}
              <option value={customer.id}>{getCustomerDisplayName(customer)}</option>
            {/each}
          </select>
        </div>
        <div>
          <label class="label">{$_('timesheets.fields.billable')}</label>
          <select bind:value={billableFilter} onchange={handleFilterChange} class="input">
            <option value="">{$_('timesheets.filters.allStatuses')}</option>
            <option value="true">{$_('timesheets.filters.billableOnly')}</option>
            <option value="false">{$_('timesheets.filters.nonBillableOnly')}</option>
          </select>
        </div>
        {#if hasFilters}
          <button class="btn btn-secondary" onclick={clearFilters}>
            {$_('common.filters')} &times;
          </button>
        {/if}
      </div>
    </div>

    <!-- Timesheets Table -->
    <div class="card overflow-hidden">
      {#if loading}
        <div class="p-12 text-center">
          <Loader size={48} class="mx-auto text-primary-500 animate-spin mb-4" />
          <p class="text-gray-500 dark:text-gray-400">{$_('common.loading')}</p>
        </div>
      {:else if error}
        <div class="p-12 text-center">
          <p class="text-red-500">{error}</p>
        </div>
      {:else if timesheets.length === 0}
        <div class="p-12 text-center">
          <Clock size={48} class="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
          <p class="text-gray-500 dark:text-gray-400 text-lg">{$_('timesheets.noData')}</p>
          <p class="text-gray-400 dark:text-gray-500 text-sm mt-1">{$_('timesheets.noDataHint')}</p>
        </div>
      {:else}
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-gray-50 dark:bg-gray-800/50">
              <tr>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('timesheets.fields.date')}</th>
                <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('timesheets.fields.hours')}</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('timesheets.fields.customer')}</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('timesheets.fields.activityType')}</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider max-w-xs">{$_('timesheets.fields.description')}</th>
                <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('timesheets.fields.billable')}</th>
                <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('timesheets.fields.hourlyRate')}</th>
                <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('timesheets.fields.billed')}</th>
                <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('common.actions')}</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
              {#each timesheets as timesheet (timesheet.id)}
                <tr class="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td class="px-4 py-3 whitespace-nowrap">
                    <div class="flex items-center space-x-2">
                      <Calendar size={16} class="text-gray-400" />
                      <span class="text-gray-900 dark:text-white">{formatDate(timesheet.date)}</span>
                    </div>
                  </td>
                  <td class="px-4 py-3 text-right font-medium text-gray-900 dark:text-white">
                    {formatHours(timesheet.hours)}h
                  </td>
                  <td class="px-4 py-3 text-gray-600 dark:text-gray-300">
                    {timesheet.customerName || '-'}
                  </td>
                  <td class="px-4 py-3">
                    {#if timesheet.activityType}
                      <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium {getActivityTypeBadgeClass(timesheet.activityType)}">
                        {$_(`timesheets.activityTypes.${timesheet.activityType}`)}
                      </span>
                    {:else}
                      -
                    {/if}
                  </td>
                  <td class="px-4 py-3 text-gray-500 dark:text-gray-400 max-w-xs truncate">
                    {timesheet.description || '-'}
                  </td>
                  <td class="px-4 py-3 text-center">
                    {#if timesheet.billable}
                      <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                        <CheckCircle size={12} class="mr-1" />
                        {$_('common.active')}
                      </span>
                    {:else}
                      <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
                        <XCircle size={12} class="mr-1" />
                        No
                      </span>
                    {/if}
                  </td>
                  <td class="px-4 py-3 text-right text-gray-600 dark:text-gray-300">
                    <div class="flex flex-col items-end gap-1">
                      <!--
                        formatCurrency already answers '-' for null/undefined,
                        so no truthiness check in front of it: a deliberate
                        0,00 (free but tracked work) is a real rate and must
                        read as € 0,00, not as "no rate" - the same hardening
                        TimesheetModal got for the very same value.
                      -->
                      <span>{formatCurrency(timesheet.hourlyRate)}</span>
                      {#if timesheet.appliedCondition}
                        <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900 dark:text-fuchsia-200">
                          {$_(`timesheets.conditions.${timesheet.appliedCondition}`)}
                        </span>
                      {/if}
                    </div>
                  </td>
                  <td class="px-4 py-3 text-center">
                    {#if timesheet.billable}
                      <button
                        class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium cursor-pointer transition-colors {timesheet.billed ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-200 dark:hover:bg-emerald-900/50' : 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 hover:bg-orange-200 dark:hover:bg-orange-900/50'}"
                        onclick={() => toggleBilled(timesheet)}
                        title={timesheet.billed ? $_('timesheets.markUnbilled') : $_('timesheets.markBilled')}
                      >
                        <Receipt size={12} class="mr-1" />
                        {timesheet.billed ? $_('timesheets.billed') : $_('timesheets.unbilled')}
                      </button>
                    {:else}
                      <span class="text-gray-400 text-xs">-</span>
                    {/if}
                  </td>
                  <td class="px-4 py-3 text-center">
                    <div class="flex items-center justify-center space-x-2">
                      <button
                        class="p-2 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        onclick={() => handleEditClick(timesheet)}
                        title={$_('common.edit')}
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        class="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        onclick={() => handleDeleteClick(timesheet)}
                        title={$_('common.delete')}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        {#if totalPages > 1}
          <div class="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <p class="text-sm text-gray-500 dark:text-gray-400">
              {$_('common.total')}: {total}
            </p>
            <div class="flex items-center space-x-2">
              <button
                class="btn btn-secondary py-1 px-3"
                disabled={currentPage <= 1}
                onclick={() => { currentPage--; loadTimesheets(); }}
              >
                {$_('common.previous')}
              </button>
              <span class="text-sm text-gray-600 dark:text-gray-400">
                {$_('common.page')} {currentPage} {$_('common.of')} {totalPages}
              </span>
              <button
                class="btn btn-secondary py-1 px-3"
                disabled={currentPage >= totalPages}
                onclick={() => { currentPage++; loadTimesheets(); }}
              >
                {$_('common.next')}
              </button>
            </div>
          </div>
        {/if}
      {/if}
    </div>
  </div>
</Layout>

<!-- Timesheet Modal -->
{#if showTimesheetModal}
  <TimesheetModal
    timesheet={editingTimesheet}
    on:success={handleTimesheetSaved}
    on:cancel={() => { showTimesheetModal = false; editingTimesheet = null; }}
  />
{/if}

<!-- Confirm Delete Modal -->
{#if showConfirmModal}
  <ConfirmModal
    title={$_('timesheets.delete')}
    message={$_('timesheets.deleteConfirm')}
    on:confirm={handleConfirmDelete}
    on:cancel={() => { showConfirmModal = false; deletingTimesheet = null; }}
  />
{/if}
